import L from "leaflet";

export class MeasureControl {
  /** Mapa Leaflet (se setea en `init`). */
  private _map!: L.Map;
  /** Botón del control. */
  private _btn: HTMLButtonElement | null = null;
  /** Modo de medición activo. */
  private _active = false;
  /** Puntos clickeados de la medición actual. */
  private _points: L.LatLng[] = [];
  /** Polilínea que une los puntos (o null). */
  private _polyline: L.Polyline | null = null;
  /** Marcadores de cada punto (cada uno con su tooltip de distancia acumulada). */
  private _markers: L.CircleMarker[] = [];
  /** Callback al activar la medición (p. ej. para deseleccionar el lote activo). */
  public onActivate: (() => void) | null = null;
  /** Handler del click del botón (nombrado para poder removerlo). */
  private _onBtnClick = (): void => this._toggle();
  /** Handler del click en el mapa (nombrado para poder removerlo). */
  private _onMapClick = (e: L.LeafletMouseEvent): void => {
    if (!this._active) return;
    this.addPoint(e.latlng);
  };

  /** Indica si el modo de medición está activo (para que el mapa apague otras
   *  interacciones, p. ej. la selección de lotes, mientras se mide). */
  get active(): boolean {
    return this._active;
  }

  /** Registra el botón en el contenedor y cablea la medición. */
  init(parent: HTMLElement, map: L.Map): void {
    this._map = map;

    const btn = L.DomUtil.create("button", "sx-map-btn") as HTMLButtonElement;
    btn.innerHTML = '<i class="ph ph-ruler"></i>';
    btn.title = "Medir distancia";
    L.DomEvent.disableClickPropagation(btn);
    L.DomEvent.on(btn, "click", this._onBtnClick);
    this._btn = btn;

    parent.appendChild(btn);

    // Escucha clicks en el mapa para agregar puntos (solo actúa si está activo).
    map.on("click", this._onMapClick);
  }

  /** Limpia la medición y quita los listeners (botón y click del mapa).
   *  No-op si nunca se llamó `init` (instancia creada pero no montada). */
  dispose(): void {
    if (!this._map) return;
    this._clearMeasurement();
    this._map.off("click", this._onMapClick);
    if (this._btn) L.DomEvent.off(this._btn, "click", this._onBtnClick);
    this._btn = null;
  }

  /** Alterna el modo medición: cambia el cursor y, al apagar, limpia la medición. */
  private _toggle(): void {
    this._active = !this._active;
    this._btn?.classList.toggle("sx-map-btn--active", this._active);

    if (this._active) {
      this._map.getContainer().style.cursor = "crosshair";
      this.onActivate?.();
    } else {
      this._map.getContainer().style.cursor = "";
      this._clearMeasurement();
    }
  }

  /** Agrega un punto: marca, actualiza la polilínea y muestra en el punto su
   *  distancia acumulada desde el inicio (el primero no lleva tooltip).
   *  Pública: también la invoca `BaseMapRender` (vía `RigthActions`) cuando el
   *  click viene de un layer de lote, ya que ahí no sirve el bubbling al mapa
   *  (Leaflet corta la propagación si el layer tiene popup). */
  addPoint(latlng: L.LatLng): void {
    this._points.push(latlng);

    const marker = L.circleMarker(latlng, {
      radius: 4,
      fillColor: "#00FFFF",
      color: "#fff",
      weight: 2,
      opacity: 1,
      fillOpacity: 1,
    }).addTo(this._map);
    this._markers.push(marker);

    if (this._points.length >= 2) {
      if (this._polyline) {
        this._polyline.setLatLngs(this._points);
      } else {
        this._polyline = L.polyline(this._points, {
          color: "#00FFFF",
          weight: 2,
          opacity: 0.8,
        }).addTo(this._map);
      }

      // Tooltip con la distancia acumulada hasta este punto.
      const cumulative = this._cumulativeTo(this._points.length - 1);
      marker.bindTooltip(this._formatDistance(cumulative), {
        permanent: true,
        direction: "top",
        offset: L.point(0, -8),
        className: "leaflet-lote-tooltip",
      });
    }
  }

  /** Suma las distancias entre puntos consecutivos hasta `index` (metros). */
  private _cumulativeTo(index: number): number {
    let total = 0;
    for (let i = 1; i <= index; i++) {
      total += this._points[i - 1].distanceTo(this._points[i]);
    }
    return total;
  }

  /** Formatea una distancia en metros como `m` o `km`. */
  private _formatDistance(meters: number): string {
    return meters >= 1000
      ? `${(meters / 1000).toFixed(2)} km`
      : `${Math.round(meters)} m`;
  }

  /** Borra puntos, polilínea, marcadores y tooltip de la medición actual. */
  private _clearMeasurement(): void {
    this._points = [];
    if (this._polyline) {
      this._map.removeLayer(this._polyline);
      this._polyline = null;
    }
    for (const m of this._markers) {
      this._map.removeLayer(m);
    }
    this._markers = [];
  }
}
